// Primitives.jsx -- Phase 3 inline primitive components
// Ported from hub-myhub-v8.jsx prototype. ASCII only. var declarations. ESM only. Inline styles.
import { useContext, useState } from 'react';
import { ThemeContext, MONO } from '../../lib/theme.js';
import { AGENT_ICON_SVG, AGENT_NAME_TO_HUBID } from '../../lib/agent-icons.js';
import { getProgressionIcon, getProgressRing, getAutonomyIcon } from '../../lib/progression-icons.js';

// Dot -- colored circle indicator
export function Dot({ color, size }) {
  return (
    <span style={{ width: size || 6, height: size || 6, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
  );
}

// AgentDot -- icon dot for agent; falls back to Dot if no icon found
function AgentDot({ agent, color, size }) {
  var theme = useContext(ThemeContext);
  var hubId = AGENT_NAME_TO_HUBID[agent];
  var iconSize = size || 8;
  var containerSize = iconSize + 8;
  if (hubId && AGENT_ICON_SVG[hubId]) {
    return (
      <div style={{ width: containerSize, height: containerSize, borderRadius: '50%', background: (color || theme.accent) + '18', color: color || theme.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: AGENT_ICON_SVG[hubId] }} />
      </div>
    );
  }
  return <Dot color={color} size={size || 6} />;
}

// Pill -- colored label badge
export function Pill({ children, color }) {
  return (
    <span style={{ fontSize: 8, fontWeight: 600, padding: '1px 6px', borderRadius: 8, background: color + '14', color: color, display: 'inline-block' }}>{children}</span>
  );
}

// MetricRow -- row of metric boxes (value + label + optional color)
export function MetricRow({ items }) {
  var theme = useContext(ThemeContext);
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
      {items.map(function(m, i) {
        return (
          <div key={i} style={{ flex: 1, background: theme.surfaceInset, borderRadius: 6, padding: '5px 6px', textAlign: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 600, fontFamily: MONO, color: m.color || theme.text, letterSpacing: -0.3 }}>{m.value}</div>
            <div style={{ fontSize: 7, color: theme.textMuted }}>{m.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// BriefCard -- colored left-border card with agent, title, body, actions
export function BriefCard({ color, agent, title, body, actions, onAction }) {
  var theme = useContext(ThemeContext);
  return (
    <div style={{ background: theme.surface, border: '0.5px solid ' + theme.border, borderRadius: 8, padding: '8px 10px', borderLeft: '2px solid ' + color, marginBottom: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
        <AgentDot agent={agent} color={color} />
        <span style={{ fontSize: 9, fontWeight: 600, color: color }}>{agent}</span>
        <Pill color={color}>{title}</Pill>
      </div>
      <div style={{ fontSize: 11, color: theme.textSecondary, lineHeight: 1.5, paddingLeft: 10, marginBottom: actions && actions.length ? 5 : 0 }}>{body}</div>
      {actions && actions.length > 0 && (
        <div style={{ display: 'flex', gap: 3, paddingLeft: 10 }}>
          {actions.map(function(a, i) {
            return (
              <span
                key={i}
                onClick={function() { if (onAction) onAction(a); }}
                style={{ fontSize: 9, padding: '2px 8px', borderRadius: 5, cursor: 'pointer', background: i === 0 ? color : 'transparent', color: i === 0 ? '#fff' : theme.textMuted, border: i === 0 ? 'none' : '0.5px solid ' + theme.border }}
              >{a}</span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ApprovalCard -- three-state: collapsed, expanded (action preview + ghost), flipped (TRAINING)
// level: 'L1' (needs approval), 'L2' (auto-handled), 'L3' (autonomous)
// trainingData: { confidence, approved, target, corrections, qualityChecks, otherSkills }
// nearPromotion: true when 80%+ toward next level
// ghostPreview: { description, trackRecord }
// onFlip: callback -- parent manages flip rendering
export function ApprovalCard({ color, agent, title, body, actions, level, onAction, trainingData, nearPromotion, ghostPreview, onFlip }) {
  var theme = useContext(ThemeContext);
  var _expanded = useState(false);
  var expanded = _expanded[0];
  var setExpanded = _expanded[1];

  var levelPill = null;
  var showActions = actions && actions.length > 0;

  if (level === 'L1') {
    levelPill = <Pill color={theme.warning}>Needs approval</Pill>;
  } else if (level === 'L2') {
    levelPill = <Pill color={theme.success}>Auto-handled</Pill>;
    if (!showActions) { actions = ['Undo']; showActions = true; }
  } else if (level === 'L3') {
    levelPill = <Pill color={theme.textMuted}>Autonomous</Pill>;
    showActions = false;
  }

  var ringPct = trainingData ? Math.round(100 * trainingData.approved / (trainingData.target || 1)) : 0;
  var remaining = trainingData ? Math.max(0, trainingData.target - trainingData.approved) : 0;

  return (
    <div data-approval-card onClick={function() { if (trainingData) setExpanded(!expanded); }} style={{ background: theme.surface, border: '0.5px solid ' + theme.border, borderRadius: 8, padding: '8px 10px', borderLeft: '2px solid ' + color, marginBottom: 4, cursor: trainingData ? 'pointer' : 'default' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3, flexWrap: 'wrap' }}>
        <AgentDot agent={agent} color={color} />
        <span style={{ fontSize: 9, fontWeight: 600, color: color }}>{agent}</span>
        <Pill color={color}>{title}</Pill>
        {levelPill}
        <div style={{ flex: 1 }} />
        {trainingData && onFlip && (
          <div data-training-tab onClick={function(e) { e.stopPropagation(); if (onFlip) onFlip(); }} style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', padding: '1px 6px', borderRadius: 6, background: color + '0a' }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: getProgressRing(ringPct) }} />
            <span style={{ fontSize: 8, color: color, fontWeight: 600 }}>TRAINING</span>
          </div>
        )}
        {nearPromotion && trainingData && (
          <span style={{ fontSize: 8, fontFamily: MONO, color: color, opacity: 0.7 }}>{trainingData.approved}/{trainingData.target}</span>
        )}
      </div>

      <div style={{ fontSize: 11, color: theme.textSecondary, lineHeight: 1.5, paddingLeft: 10, marginBottom: showActions ? 5 : 0 }}>{body}</div>

      {showActions && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 3, paddingLeft: 10 }}>
          {actions.map(function(a, i) {
            return (
              <span key={i} onClick={function(e) { e.stopPropagation(); if (onAction) onAction(a); }} style={{ fontSize: 9, padding: '2px 8px', borderRadius: 5, cursor: 'pointer', background: i === 0 ? color : 'transparent', color: i === 0 ? '#fff' : theme.textMuted, border: i === 0 ? 'none' : '0.5px solid ' + theme.border }}>{a}</span>
            );
          })}
          <div style={{ flex: 1 }} />
          {trainingData && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0 }}><polyline points="6,9 12,15 18,9" /></svg>}
        </div>
      )}

      {expanded && trainingData && (
        <div data-approval-expanded style={{ marginTop: 6, paddingLeft: 10 }}>
          <div style={{ fontSize: 9, fontWeight: 600, color: theme.textMuted, marginBottom: 4 }}>What each action does</div>
          <div style={{ fontSize: 10, color: theme.textSecondary, lineHeight: 1.6, marginBottom: 4 }}>
            <strong style={{ color: theme.text }}>Approve:</strong> Confirms this is correct. {agent} uses it to learn your preferences.<br/>
            <strong style={{ color: theme.text }}>Edit:</strong> You fix the answer. {agent} learns from the correction.<br/>
            <strong style={{ color: theme.text }}>Reject:</strong> Flags this as wrong. {agent} avoids this pattern.
          </div>

          {nearPromotion && ghostPreview && (
            <div data-ghost-preview style={{ marginTop: 6, padding: '6px 8px', borderRadius: 6, background: color + '08', border: '0.5px dashed ' + color + '30' }}>
              <div style={{ fontSize: 9, fontWeight: 600, color: color, marginBottom: 3 }}>Here is what {agent} would have done on its own</div>
              <div style={{ fontSize: 10, color: theme.textSecondary, lineHeight: 1.5, marginBottom: 4 }}>{ghostPreview.description}</div>
              <div style={{ fontSize: 9, color: theme.textMuted, marginBottom: 4 }}>{ghostPreview.trackRecord}</div>
              <div style={{ fontSize: 9, color: color }}>{remaining} more approval{remaining !== 1 ? 's' : ''} and {agent} qualifies. Before it goes live, Hub verifies accuracy and your admin reviews.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// TrainingCardBack -- flip side of ApprovalCard showing training detail
// Rendered by parent when user clicks TRAINING tab
export function TrainingCardBack({ color, agent, trainingData, onFlipBack }) {
  var theme = useContext(ThemeContext);
  if (!trainingData) return null;
  var conf = trainingData.confidence || 0;
  var confColor = conf >= 90 ? theme.success : conf >= 80 ? theme.warning : theme.critical;
  var confLabel = conf < 80 ? 'Needs work' : conf + '%';
  var checksColor = trainingData.qualityChecks === 'passing' ? theme.success : theme.warning;
  return (
    <div data-training-back style={{ background: theme.surface, border: '0.5px solid ' + theme.border, borderRadius: 8, padding: '8px 10px', borderLeft: '2px solid ' + color, marginBottom: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
        <div style={{ width: 18, height: 18, borderRadius: '50%', background: color + '18', color: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: getAutonomyIcon(1) }} />
        </div>
        <span style={{ fontSize: 10, fontWeight: 600, color: theme.text, flex: 1 }}>{agent} Training</span>
        <span onClick={function() { if (onFlipBack) onFlipBack(); }} style={{ fontSize: 8, color: theme.textMuted, cursor: 'pointer', padding: '1px 6px', borderRadius: 4, background: theme.surfaceInset }}>Back</span>
      </div>
      <div style={{ fontSize: 10, color: theme.textSecondary, lineHeight: 1.5, marginBottom: 8 }}>Every time you approve or correct, {agent} gets better at matching your preferences.</div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        <div style={{ flex: 1, background: theme.surfaceInset, borderRadius: 6, padding: '5px 6px', textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 600, fontFamily: MONO, color: confColor }}>{confLabel}</div>
          <div style={{ fontSize: 7, color: theme.textMuted }}>Confidence</div>
        </div>
        <div style={{ flex: 1, background: theme.surfaceInset, borderRadius: 6, padding: '5px 6px', textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 600, fontFamily: MONO, color: theme.text }}>{trainingData.approved}<span style={{ fontSize: 9, color: theme.textMuted }}>/{trainingData.target}</span></div>
          <div style={{ fontSize: 7, color: theme.textMuted }}>Approved</div>
        </div>
        <div style={{ flex: 1, background: theme.surfaceInset, borderRadius: 6, padding: '5px 6px', textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 600, fontFamily: MONO, color: theme.text }}>{trainingData.corrections}</div>
          <div style={{ fontSize: 7, color: theme.textMuted }}>Your corrections</div>
        </div>
      </div>
      {/* VEC-S11: Recent corrections list */}
      {Array.isArray(trainingData.recentCorrections) && trainingData.recentCorrections.length > 0 && (
        <div style={{ marginTop: 6, borderTop: '0.5px solid ' + theme.border, paddingTop: 4 }}>
          <div style={{ fontSize: 8, fontWeight: 600, color: theme.textMuted, marginBottom: 3 }}>Recent corrections</div>
          {trainingData.recentCorrections.slice(0, 3).map(function(c, ci) {
            return (
              <div key={ci} style={{ fontSize: 9, color: theme.textSecondary, padding: '2px 0', borderBottom: ci < Math.min(trainingData.recentCorrections.length, 3) - 1 ? '0.5px solid ' + theme.border : 'none' }}>
                <span style={{ color: theme.textMuted }}>{c.date || ''}</span> {c.text || c.correction || ''}
              </div>
            );
          })}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: checksColor, flexShrink: 0 }} />
        <span style={{ fontSize: 9, color: theme.textSecondary }}>Quality checks: {trainingData.qualityChecks}</span>
      </div>
      {trainingData.otherSkills && trainingData.otherSkills.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <div style={{ fontSize: 8, fontWeight: 600, color: theme.textMuted, marginBottom: 3 }}>Also learning</div>
          {trainingData.otherSkills.map(function(skill, si) {
            return <div key={si} style={{ fontSize: 9, color: theme.textSecondary, paddingLeft: 6 }}>{skill}</div>;
          })}
        </div>
      )}
    </div>
  );
}

// ProgressionTip -- smart recommendation card that leads with why
// Why text always visible. Click expands to show NOW vs WITH + trust ramp.
// Action button inside expanded area (click does not collapse card).
export function ProgressionTip({ color, agent, title, body, detail, action, effort, onAction }) {
  var theme = useContext(ThemeContext);
  var _tipOpen = useState(false);
  var tipOpen = _tipOpen[0];
  var setTipOpen = _tipOpen[1];
  var tipColor = color || theme.accent;
  var hubId = AGENT_NAME_TO_HUBID[agent];
  return (
    <div data-progression-tip onClick={function() { setTipOpen(!tipOpen); }} style={{ background: theme.surface, border: '0.5px solid ' + theme.border, borderRadius: 8, padding: '8px 10px', borderLeft: '2px solid ' + tipColor, marginBottom: 4, cursor: 'pointer' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
        {hubId && AGENT_ICON_SVG[hubId] ? (
          <div style={{ width: 14, height: 14, borderRadius: '50%', background: tipColor + '18', color: tipColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: AGENT_ICON_SVG[hubId] }} />
          </div>
        ) : null}
        <span style={{ fontSize: 9, fontWeight: 600, color: tipColor }}>{agent}</span>
        <Pill color={tipColor}>{title}</Pill>
        {effort && <Pill color={theme.textMuted}>{effort}</Pill>}
      </div>
      <div style={{ fontSize: 11, color: theme.textSecondary, lineHeight: 1.5, paddingLeft: 10 }}>{body}</div>
      {tipOpen && detail && (
        <div data-tip-detail style={{ marginTop: 6, paddingTop: 6, borderTop: '0.5px solid ' + theme.border, paddingLeft: 10 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <div style={{ flex: 1, padding: '5px 7px', borderRadius: 6, background: theme.surfaceInset }}>
              <div style={{ fontSize: 8, fontWeight: 600, color: theme.textMuted, marginBottom: 2 }}>NOW</div>
              <div style={{ fontSize: 10, color: theme.textSecondary, lineHeight: 1.5 }}>{detail.now}</div>
            </div>
            <div style={{ flex: 1, padding: '5px 7px', borderRadius: 6, background: tipColor + '08', border: '0.5px solid ' + tipColor + '20' }}>
              <div style={{ fontSize: 8, fontWeight: 600, color: tipColor, marginBottom: 2 }}>WITH HUB</div>
              <div style={{ fontSize: 10, color: theme.textSecondary, lineHeight: 1.5 }}>{detail.with}</div>
            </div>
          </div>
          {detail.trustRamp && (
            <div style={{ fontSize: 9, color: theme.textMuted, lineHeight: 1.5, marginBottom: 6 }}>{detail.trustRamp}</div>
          )}
          {action && (
            <span onClick={function(e) { e.stopPropagation(); if (onAction) onAction(action); }} style={{ fontSize: 9, padding: '3px 10px', borderRadius: 5, cursor: 'pointer', background: tipColor, color: '#fff', display: 'inline-block' }}>{action}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ProgressionEvent -- conversation event for level-ups, unlocks, milestones
// Props: icon (svg string), color, agent, title, pills (array), body, comparison (optional)
export function ProgressionEvent({ icon, color, agent, title, pills, body, comparison }) {
  var theme = useContext(ThemeContext);
  var eventColor = color || theme.accent;
  var hubId = AGENT_NAME_TO_HUBID[agent];
  return (
    <div data-progression-event style={{ background: theme.surface, border: '0.5px solid ' + theme.border, borderRadius: 8, padding: '8px 10px', borderLeft: '2px solid ' + eventColor, marginBottom: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
        <div style={{ width: 16, height: 16, borderRadius: '50%', background: eventColor + '18', color: eventColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: icon || getProgressionIcon('milestone_outline') }} />
        </div>
        {hubId && AGENT_ICON_SVG[hubId] ? (
          <div style={{ width: 14, height: 14, borderRadius: '50%', background: eventColor + '18', color: eventColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: AGENT_ICON_SVG[hubId] }} />
          </div>
        ) : null}
        <span style={{ fontSize: 9, fontWeight: 600, color: eventColor }}>{agent}</span>
        {(pills || []).map(function(p, i) {
          return <Pill key={i} color={eventColor}>{p}</Pill>;
        })}
      </div>
      <div style={{ fontSize: 11, fontWeight: 500, color: theme.text, paddingLeft: 10, marginBottom: 2 }}>{title}</div>
      {body && <div style={{ fontSize: 10, color: theme.textSecondary, lineHeight: 1.5, paddingLeft: 10 }}>{body}</div>}
      {comparison && (
        <div data-progression-comparison style={{ fontSize: 9, color: theme.textMuted, paddingLeft: 10, marginTop: 3, fontFamily: MONO }}>{comparison}</div>
      )}
    </div>
  );
}

// LevelUpCard -- celebration card for agent level promotion
export function LevelUpCard(props) {
  var theme = props.theme || {};
  var ok = theme.success || '#10B981';
  var okL = '#ECFDF5';
  var agentName = props.agent || 'Agent';
  var newLevel = props.level || 2;
  var levelLabels = { 1: 'Learning', 2: 'Trusted', 3: 'Autonomous', 4: 'Expert' };
  var levelLabel = levelLabels[newLevel] || 'Trusted';
  var changes = props.changes || [];
  var stats = props.stats || '';
  var MONO = theme.mono || "'IBM Plex Mono', monospace";

  return (
    <div style={{
      background: okL, border: '0.5px solid ' + ok + '30',
      borderRadius: 8, borderLeft: '3px solid ' + ok,
      padding: '14px 16px', marginBottom: 6
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={ok} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="18,15 12,9 6,15" />
        </svg>
        <span style={{ fontSize: 10, fontWeight: 700, color: ok }}>LEVEL UP</span>
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: theme.text || '#12110f', marginBottom: 8 }}>
        {agentName} reached Level {newLevel}: {levelLabel}
      </div>
      {changes.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: ok, marginBottom: 4 }}>What changed:</div>
          {changes.map(function(c, i) {
            return (
              <div key={i} style={{ fontSize: 12, color: theme.text || '#12110f', lineHeight: 1.8, paddingLeft: 8 }}>
                {c}
              </div>
            );
          })}
        </div>
      )}
      {stats && (
        <div style={{ fontSize: 11, color: theme.textSecondary || '#6b6962', lineHeight: 1.6, padding: '6px 0', borderTop: '0.5px solid ' + ok + '30' }}>
          {stats}
        </div>
      )}
      <div style={{ fontSize: 13, fontWeight: 600, color: '#0F6E56', marginTop: 4 }}>
        Thank you for teaching {agentName}. It gets better from here.
      </div>
    </div>
  );
}

// StatusUpdate -- single row: dot + agent name + text + status pill
// status: 'complete' | 'in_progress' | 'blocked' | 'waiting'
export function StatusUpdate({ agent, text, status, color }) {
  var theme = useContext(ThemeContext);

  var statusColor = theme.textMuted;
  var statusLabel = status;
  if (status === 'complete')    { statusColor = theme.success;   statusLabel = 'Complete'; }
  if (status === 'in_progress') { statusColor = theme.accent;    statusLabel = 'In progress'; }
  if (status === 'blocked')     { statusColor = theme.critical;  statusLabel = 'Blocked'; }
  if (status === 'waiting')     { statusColor = theme.warning;   statusLabel = 'Waiting'; }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 0' }}>
      <AgentDot agent={agent} color={color || statusColor} />
      <span style={{ fontSize: 10, fontWeight: 600, color: color || statusColor }}>{agent}</span>
      <span style={{ fontSize: 10, color: theme.textSecondary, flex: 1 }}>{text}</span>
      <Pill color={statusColor}>{statusLabel}</Pill>
    </div>
  );
}

export function ToolNudgeCard(props) {
  var theme = props.theme || {};
  var accent = theme.accent || '#4F6EF7';
  var accentLight = theme.accentLight || '#EEF1FE';
  var items = props.items || [];
  var MONO = theme.mono || "'IBM Plex Mono', monospace";
  var SRC_ICONS = { gmail: 'M', slack: 'S', calendar: 'C', quickbooks: 'Q', ramp: 'R' };
  var SRC_COLORS = { gmail: '#EA4335', slack: '#4A154B', calendar: '#4285F4', quickbooks: '#2CA01C', ramp: '#1A1A1A' };

  return (
    <div style={{
      background: accentLight, border: '0.5px solid ' + accent + '25',
      borderRadius: 8, borderLeft: '2px solid ' + accent,
      padding: '10px 14px', marginBottom: 6
    }}>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: accent, marginBottom: 6 }}>From Your Tools</div>
      {items.map(function(item, i) {
        var src = (item.source || '').toLowerCase();
        var srcIcon = SRC_ICONS[src] || src.charAt(0).toUpperCase();
        var srcColor = SRC_COLORS[src] || theme.textMuted || '#8b8880';
        return (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '5px 0',
            borderTop: i > 0 ? '0.5px solid ' + accent + '15' : 'none'
          }}>
            <div style={{
              width: 20, height: 20, borderRadius: 5,
              background: srcColor + '15',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 9, fontWeight: 700, color: srcColor, flexShrink: 0
            }}>{srcIcon}</div>
            <span style={{ fontSize: 12, color: theme.text || '#12110f', flex: 1 }}>{item.text}</span>
            {item.action && (
              <span
                onClick={function() { if (props.onAction) props.onAction(item); }}
                style={{ fontSize: 10, color: accent, fontWeight: 600, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}
              >{item.action}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// HandledCard -- collapsible summary of autonomous agent actions
export function HandledCard({ agents, theme: themeProp }) {
  var theme = themeProp || useContext(ThemeContext);
  var _open = useState(false);
  var open = _open[0]; var setOpen = _open[1];

  if (!agents || agents.length === 0) return null;
  var totalItems = agents.reduce(function(sum, a) { return sum + (a.items ? a.items.length : 0); }, 0);
  if (totalItems === 0) return null;

  return (
    <div onClick={function() { setOpen(!open); }} style={{ background: theme.surface, border: '1px solid ' + (theme.success || '#10B981') + '30', borderRadius: 8, borderLeft: '3px solid ' + (theme.success || '#10B981'), marginBottom: 4, cursor: 'pointer' }}>
      <div style={{ padding: '10px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: (theme.successLight || '#ecfdf5'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.success || '#10B981'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 16, fontWeight: 700, fontFamily: MONO, color: theme.success || '#10B981' }}>{totalItems}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: theme.text }}>Handled Autonomously</span>
            </div>
            <div style={{ fontSize: 9, color: theme.textMuted, marginTop: 1 }}>since last Brief · {agents.length} agent{agents.length > 1 ? 's' : ''} active overnight</div>
          </div>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={theme.success || '#10B981'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0 }}><polyline points="6,9 12,15 18,9" /></svg>
        </div>
      </div>
      {open && (
        <div onClick={function(e) { e.stopPropagation(); }} style={{ padding: '0 12px 10px' }}>
          {agents.map(function(agent) {
            return (
              <div key={agent.name} style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <AgentDot agent={agent.name} color={agent.color} />
                  <span style={{ fontSize: 10, fontWeight: 600, color: agent.color || theme.accent }}>{agent.name}</span>
                  <span style={{ fontSize: 8, fontFamily: MONO, color: theme.textMuted, marginLeft: 'auto' }}>{agent.items.length} action{agent.items.length > 1 ? 's' : ''}</span>
                </div>
                {agent.items.map(function(item, idx) {
                  return (
                    <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '3px 0 3px 24px' }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: theme.success || '#10B981', marginTop: 5, flexShrink: 0 }} />
                      <span style={{ fontSize: 10, color: theme.textSecondary, lineHeight: 1.5, flex: 1 }}>{item.text}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ConnectToolsCard(props) {
  var theme = props.theme || {};
  var accent = theme.accent || '#4F6EF7';
  var personalTools = props.personalTools || [];
  var orgTools = props.orgTools || [];
  var onConnect = props.onConnect || function() {};

  // Legacy compat: if old tools prop passed, treat as personal
  if (props.tools && !props.personalTools) personalTools = props.tools;

  var allTools = personalTools.concat(orgTools);
  var unconnected = allTools.filter(function(t) { return !t.connected; });
  if (unconnected.length === 0) return null;

  var connectedColor = theme.success || '#10B981';

  function renderTool(tool, i) {
    return (
      <div key={tool.id} style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '7px 0',
        borderTop: i > 0 ? '0.5px solid ' + (theme.border || '#e5e3dc') : 'none'
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: tool.connected ? connectedColor + '12' : tool.color + '12',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 14, color: tool.connected ? connectedColor : tool.color, flexShrink: 0
        }}>{tool.icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.text || '#12110f' }}>{tool.label}</div>
          <div style={{ fontSize: 11, color: theme.textMuted || '#8b8880' }}>{tool.desc}</div>
        </div>
        {tool.connected ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: connectedColor }}>
            <span style={{ fontSize: 13 }}>{String.fromCharCode(10003)}</span> Connected
          </div>
        ) : tool.apiKey ? (
          <div style={{
            padding: '5px 10px', borderRadius: 6,
            background: (theme.surfaceInset || '#f0efe9'), color: theme.textSecondary || '#6b6962',
            fontSize: 10, fontWeight: 600
          }}>API Key</div>
        ) : (
          <div
            onClick={function() { onConnect(tool.id); }}
            style={{
              padding: '5px 14px', borderRadius: 6,
              background: accent, color: '#fff',
              fontSize: 11, fontWeight: 600, cursor: 'pointer'
            }}
          >Connect</div>
        )}
      </div>
    );
  }

  function renderSection(label, tools) {
    if (!tools || tools.length === 0) return null;
    return (
      <div>
        <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: theme.textMuted || '#8b8880', marginBottom: 6, marginTop: 2 }}>
          {label}
        </div>
        {tools.map(function(tool, i) { return renderTool(tool, i); })}
      </div>
    );
  }

  return (
    <div style={{
      background: theme.surface, border: '0.5px solid ' + (theme.border || '#e5e3dc'),
      borderRadius: 8, padding: '12px 14px', marginBottom: 6
    }}>
      {renderSection('Your tools', personalTools)}
      {orgTools.length > 0 && personalTools.length > 0 ? (
        <div style={{ borderTop: '0.5px solid ' + (theme.border || '#e5e3dc'), marginTop: 8, paddingTop: 8 }} />
      ) : null}
      {renderSection('Company tools (admin)', orgTools)}
    </div>
  );
}
